import React from 'react';
import styled from 'styled-components';
import SwiperCore, { Autoplay } from 'swiper/core';
import { Swiper, SwiperSlide } from 'swiper/react';
import Cursor from '../global/cursor/Cursor';
import Richtext from '../global/richtext/Richtext';
import useWindowSize from '../../hooks/useWindowSize';
import getBreakpointFlags from '../../helpers/get-breakpoint-flags';
import style from './featured-articles.module.scss';
import { clampVW } from '../../helpers/clapm';

SwiperCore.use([Autoplay]);

type CustomStyleProps = {
  backgroundColor: string;
  dateColor: string;
  timerColor: string;
  contentColor: string;
  sliderDelay: number;
  backgroundImage: any;
};

type ArticlesStyleProps = {
  logoMask: string;
  logoHeight: string;
  logoWidth: string;
  iconColor: string;
};

const handleSwiper = (swiper: any) => {
  const swiperReference = swiper?.$el?.[0];

  if (!swiperReference) return;

  swiperReference.addEventListener('mouseleave', () => {
    swiper.slideNext();
  });
};

const toString = (pre: string, attr: any, post: string) =>
  `${pre || ''}${attr}${post || ''}`;

const CustomStyles = styled.div`
  .swiper-slide.swiper-slide-active:after {
    animation-duration: ${(customStyleProps: CustomStyleProps) =>
      customStyleProps.sliderDelay}ms;
    background-color: ${(customStyleProps: CustomStyleProps) =>
      customStyleProps.timerColor};
  }

  .swiper-slide:first-child::before {
    background-color: ${(customStyleProps: CustomStyleProps) =>
      customStyleProps.backgroundColor};
    background-image: ${(customStyleProps: CustomStyleProps) =>
      customStyleProps.backgroundImage};
  }

  .${style['features-article__swiper-wrapper']} {
    background-color: ${(customStyleProps: CustomStyleProps) =>
      customStyleProps.backgroundColor};
    background-image: ${(customStyleProps: CustomStyleProps) =>
      customStyleProps.backgroundImage};
  }

  .${style['features-article__slide-container']} {
    background-color: ${(customStyleProps: CustomStyleProps) =>
      customStyleProps.backgroundColor};
    background-image: ${(customStyleProps: CustomStyleProps) =>
      customStyleProps.backgroundImage};
    color: ${(customStyleProps: CustomStyleProps) =>
      customStyleProps.dateColor};
  }

  .${style['features-article__slide-container-date']} {
    color: ${(customStyleProps: CustomStyleProps) =>
      customStyleProps.dateColor};
  }
`;

const ArticleStyles = styled.div`
  .${style['features-article__slide-container-logo']} {
    height: ${(customArticleProps: ArticlesStyleProps) =>
      toString('', customArticleProps.logoHeight, 'px')};
    width: ${(customArticleProps: ArticlesStyleProps) =>
      toString('', customArticleProps.logoWidth, 'px')};
    -webkit-mask: ${(customArticleProps: ArticlesStyleProps) =>
      toString('url(', customArticleProps.logoMask, ') no-repeat center')};
    mask: ${(customArticleProps: ArticlesStyleProps) =>
      toString('url(', customArticleProps.logoMask, ') no-repeat center')};
    background-color: ${(customArticleProps: ArticlesStyleProps) =>
      customArticleProps.iconColor};
  }
`;

const FeaturedArticles = (props: any) => {
  const { data } = props;
  const { articles } = data;

  const articlesSlide = articles.map((article: any) => {
    return (
      <SwiperSlide key={article?.id}>
        <ArticleStyles
          iconColor={data.iconColor}
          logoMask={article?.icon?.src}
          logoHeight={article?.icon?.height}
          logoWidth={article?.icon?.width}
        >
          <div className={style['features-article__slide-container']}>
            <div
              className={
                style['features-article__slide-container-logo-container']
              }
            >
              <div
                className={style['features-article__slide-container-logo']}
                aria-hidden
                role="img"
              />
            </div>
            <span className={style['features-article__slide-container-date']}>
              {article.date}
            </span>
            {article.description && (
              <Richtext
                data={article.description}
                customClasses={{
                  paragraph: style['features-article__slide-container-content']
                }}
              />
            )}
          </div>
        </ArticleStyles>
      </SwiperSlide>
    );
  });

  const getSwiperOffset = () => clampVW(24, 5.5, 80);

  const getSlidesPerView = () => {
    const { width } = useWindowSize();
    const { isMobile } = getBreakpointFlags(width);
    const offset = getSwiperOffset();
    const slidesPerView = isMobile ? 1 : 2;

    // This formula gets the percentage number of the offset based on the screen with, then converts that percentage to decimals
    let extraToMatchOffsetBefore =
      (offset * slidesPerView) / ((width - offset) / 2);
    extraToMatchOffsetBefore =
      Math.ceil((extraToMatchOffsetBefore + Number.EPSILON) * 100) / 100; // round to 2 decimals

    return slidesPerView + extraToMatchOffsetBefore;
  };

  return (
    <div className={style['features-article']}>
      <CustomStyles
        backgroundColor={
          data.backgroundTexture ? 'transparent' : data.backgroundColor
        }
        dateColor={data.dateColor}
        timerColor={data.timerColor}
        contentColor={data.contentColor}
        sliderDelay={data.sliderDelay}
        backgroundImage={
          data.backgroundTexture ? `url(${data.backgroundTexture.src})` : 'none'
        }
      >
        <div className={style.articles}>
          <Cursor data={data.cursor}>
            <div className={style['features-article__swiper-wrapper']}>
              <Swiper
                loop
                direction="horizontal"
                slidesPerView={getSlidesPerView()}
                spaceBetween={0}
                speed={500}
                onSwiper={handleSwiper}
                slidesOffsetBefore={getSwiperOffset()}
                autoplay={{
                  delay: data.sliderDelay,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true
                }}
              >
                {articlesSlide}
              </Swiper>
            </div>
          </Cursor>
        </div>
      </CustomStyles>
    </div>
  );
};

export default FeaturedArticles;
